import { Alert as ChakraAlert } from '@chakra-ui/react'
import * as React from 'react'

import {
  AlertCheckIcon,
  AlertErrorIcon,
  AlertInfoIcon,
  AlertWarnIcon,
} from '../icon'
import { CloseButton } from './close-button'

export interface AlertProps
  extends Omit<ChakraAlert.RootProps, 'status' | 'title'> {
  type: 'toast' | 'skeleton'
  status: Exclude<ChakraAlert.RootProps['status'], 'neutral'>
  title?: React.ReactNode
  closable?: boolean
  onClose?: () => void
}

export const Alert = React.forwardRef<HTMLDivElement, AlertProps>(
  function Alert(props, ref) {
    const { type, title, children, status, closable, onClose, ...rest } = props

    let statusColor = ''
    switch (status) {
      case 'success':
        statusColor = 'green'
        break
      case 'info':
        statusColor = 'blue'
        break
      case 'warning':
        statusColor = 'orange'
        break
      case 'error':
        statusColor = 'red'
        break
    }

    let iconColor: string | undefined = undefined
    let titleColor: string | undefined = undefined
    let descriptionColor: string | undefined = undefined
    let backgroundColor: string | undefined = undefined
    switch (type) {
      case 'toast':
        iconColor = 'white'
        titleColor = 'white'
        descriptionColor = 'white'
        backgroundColor = `${statusColor}.500`
        break
      case 'skeleton':
        iconColor = `${statusColor}.500`
        titleColor = `${statusColor}.500`
        descriptionColor = 'gray.700'
        backgroundColor = '#FFFFFF00'
    }

    let statusIcon: React.ReactNode = null
    switch (status) {
      case 'success':
        statusIcon = <AlertCheckIcon />
        break
      case 'info':
        statusIcon = <AlertInfoIcon />
        break
      case 'warning':
        statusIcon = <AlertWarnIcon />
        break
      case 'error':
        statusIcon = <AlertErrorIcon />
        break
    }

    return (
      <ChakraAlert.Root
        ref={ref}
        status={status}
        bgColor={backgroundColor}
        paddingBlock={3}
        {...rest}
      >
        <ChakraAlert.Indicator color={iconColor} boxSize="24px">
          {statusIcon}
        </ChakraAlert.Indicator>
        {children ? (
          <ChakraAlert.Content fontSize="md">
            <ChakraAlert.Title color={titleColor} fontWeight="bold">
              {title}
            </ChakraAlert.Title>
            <ChakraAlert.Description color={descriptionColor}>
              {children}
            </ChakraAlert.Description>
          </ChakraAlert.Content>
        ) : (
          <ChakraAlert.Title flex="1">{title}</ChakraAlert.Title>
        )}
        {closable && (
          <CloseButton
            size="sm"
            pos="relative"
            top="-2"
            insetEnd="-2"
            alignSelf="flex-start"
            onClick={onClose}
          />
        )}
      </ChakraAlert.Root>
    )
  },
)
